import { ChangeDetectionStrategy, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { take } from 'rxjs/operators';
import { DateTime } from 'luxon';
import { CopyToClipboardDirective } from '../directives/button.directive';
import { E2eIdDirective } from '../directives/e2e-id.directive';
import { LegfiDatePipe } from '../../../../pipes/format/legfi-date.pipe';
import { PrintService } from '../../../../services/print.service';
import { MembersService } from '../../../../services/members/members.service';
import { LegFiJwtService } from '../../../../services/auth/legfi-jwt.service';
import { Membership } from '../../../../models/entities/membership';

/**
 * Presentational show-once recovery-codes panel (SD-8082): the codes themselves, the copy /
 * download utilities and the "I have saved these" acknowledgement.
 *
 * Deliberately headerless and footerless so it can be embedded by either host without a second
 * dialog surface: inline as the final stage of the 2FA setup steps (enrollment), and inside
 * {@link RecoveryCodesDialogComponent} for the Settings regenerate path. The host owns the
 * heading and the action button, and gates that button on {@link acknowledgedChange}.
 */
@UntilDestroy()
@Component({
    selector: 'app-recovery-codes-panel',
    templateUrl: './recovery-codes-panel.component.html',
    styleUrls: ['./recovery-codes-panel.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgFor,
        NgIf,
        CopyToClipboardDirective,
        E2eIdDirective,
        LegfiDatePipe,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class RecoveryCodesPanelComponent implements OnInit
{
    /** Instance counter — the print source needs a DOM id and two panels could coexist. */
    private static _instances = 0;

    @Input() codes: string[] = [];

    /**
     * Account the printed sheet belongs to (SD-8983), resolved by {@link loadAccountEmail}.
     * Empty until it arrives, and the line is omitted entirely if it never does.
     */
    accountEmail = '';

    /** Emits whenever the user toggles the acknowledgement, so the host can gate its action. */
    @Output() acknowledgedChange = new EventEmitter<boolean>();

    acknowledged = false;

    /**
     * Stamped once at construction. The codes are minted immediately before this panel renders
     * and are shown exactly once, so construction time IS the generation time — and pinning it
     * keeps the printed date stable no matter how long the sheet sits open before printing.
     *
     * Held as an ISO STRING, not a Date, and that is load-bearing: `legfiDate` formats moment
     * objects, strings and numbers, but a raw `Date` matches none of its branches and falls
     * through `return value` unformatted — which printed the full
     * "Thu Aug 13 2026 05:16:57 GMT-0400 (Eastern Daylight Time)" on the sheet. The ISO string
     * takes the pipe's UTC-parse path and lands in the org's timezone.
     */
    readonly generatedAt = new Date().toISOString();

    /** DOM id of the hidden print source, unique per instance (PrintService looks it up by id). */
    readonly printId = `recovery-codes-print-${++RecoveryCodesPanelComponent._instances}`;

    /** The acknowledgement checkbox's id, unique per instance so its label points at the right one. */
    readonly acknowledgeId = `recovery-codes-acknowledge-${RecoveryCodesPanelComponent._instances}`;

    constructor(
            private _print: PrintService,
            private _members: MembersService,
            private _cd: ChangeDetectorRef,
    ) {
    }

    ngOnInit(): void {
        this.loadAccountEmail();
    }

    /**
     * Resolve the account the codes belong to, for the printed sheet (SD-8983).
     *
     * Read here rather than threaded down from the hosts because the email is NOT available
     * upstream: the JWT carries only givenNames/familyName/id/memberId/orgId, and no service
     * exposes the current user's email. Of the four surfaces that reach this panel, only the
     * Settings regenerate path holds a membership — both enrollment setup steps and their hosts
     * (the Settings setup dialog and the mandatory-2FA gate dialog) have no user context at all,
     * so they would each have needed this same fetch. One read here beats four.
     *
     * Deliberately best-effort: a labelled sheet is nicer, but the codes are the point. A failed
     * or missing read just omits the line (the template `*ngIf`s it) rather than blocking print.
     */
    private loadAccountEmail(): void {
        const jwt = LegFiJwtService.read();
        if (!jwt) {
            return;
        }

        this._members.getMember(jwt.orgId, jwt.memberId)
                .pipe(take(1), untilDestroyed(this))
                .subscribe({
                    next: (membership: Membership) => {
                        this.accountEmail = membership?.user?.email ?? '';
                        // OnPush: the panel renders before this resolves, so ask for a re-check.
                        this._cd.markForCheck();
                    },
                    error: () => {
                        // Leave the line off — printing the codes matters more than labelling them.
                    },
                });
    }

    /** Newline-joined form, used for copy-all and the downloaded text file. */
    get codesBlock(): string {
        return this.codes.join('\n');
    }

    /** trackBy for the codes list — codes are unique within a set. */
    trackByCode(_: number, code: string): string {
        return code;
    }

    onAcknowledgedChange(checked: boolean): void {
        this.acknowledged = checked;
        this.acknowledgedChange.emit(checked);
    }

    /**
     * Named for when the set was shown (SD-11060), in the date-stamped style other exports use
     * (`activity-log-2026-10-14.csv`), plus the time: a user can generate several sets in a day, and a
     * bare name piles up as "(1)", "(2)" with nothing saying which is current.
     */
    get downloadFilename(): string {
        // In the org's time zone, as the printed sheet's `legfiDate` renders the same value (F-0f33), so
        // the file and the sheet for one set never carry different days.
        const stamp = DateTime.fromISO(this.generatedAt, {zone: 'utc'})
            .setZone(LegFiJwtService.getTimezone())
            .toFormat('yyyy-MM-dd-HHmm');

        return `payhoa-recovery-codes-${stamp}.txt`;
    }

    /** Download the codes as a plain-text file so the user has an offline copy. */
    download(): void {
        const blob = new Blob([this.codesBlock + '\n'], {type: 'text/plain'});
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = this.downloadFilename;
        // A programmatic click on a detached anchor is unreliable in Firefox — attach, click, detach.
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
    }

    /**
     * Print the codes as a labelled sheet the user can file — or save as PDF from the browser's
     * own print dialog (SD-8983). The plain .txt above prints as an unlabelled list with nothing
     * identifying the account.
     *
     * Reuses PrintService rather than adding a PDF library: it clones the source element, strips
     * `.d-print-none`, and writes the result into a popup. That keeps the plaintext codes entirely
     * in the browser — they are shown exactly once and must never be sent to the server to be
     * rendered, which any server-side PDF path would require.
     */
    print(): void {
        this._print.printElement('PayHOA recovery codes', this.printId);
    }
}
